import * as React from "react"

import * as SheetPrimitive from "@radix-ui/react-dialog"
import { type VariantProps, cva } from "class-variance-authority"
import { X } from "lucide-react"

import { OVERLAY_BORDER, OVERLAY_SCRIM, PANEL_WIDTH } from "@/components/ui/overlay"
import { cn } from "@/lib/utils"

const Sheet = SheetPrimitive.Root

const SheetTrigger = SheetPrimitive.Trigger

const SheetClose = SheetPrimitive.Close

const SheetPortal = SheetPrimitive.Portal

const SheetOverlay = React.forwardRef<
  React.ElementRef<typeof SheetPrimitive.Overlay>,
  React.ComponentPropsWithoutRef<typeof SheetPrimitive.Overlay>
>(({ className, ...props }, ref) => (
  <SheetPrimitive.Overlay
    className={cn(
      `fixed inset-0 z-50 ${OVERLAY_SCRIM} data-[state=open]:animate-in data-[state=closed]:animate-out data-[state=closed]:fade-out-0 data-[state=open]:fade-in-0`,
      className,
    )}
    {...props}
    ref={ref}
  />
))
SheetOverlay.displayName = SheetPrimitive.Overlay.displayName

/** Width is a variant, not a callsite class: tailwind-merge won't drop a `max-w-*` for a `w-*`. */
const sheetVariants = cva(
  "fixed z-50 gap-4 bg-background p-5 shadow-lg transition ease-in-out data-[state=open]:animate-in data-[state=closed]:animate-out data-[state=closed]:duration-300 data-[state=open]:duration-500",
  {
    variants: {
      side: {
        top: `inset-x-0 top-0 border-b ${OVERLAY_BORDER} data-[state=closed]:slide-out-to-top data-[state=open]:slide-in-from-top`,
        bottom: `inset-x-0 bottom-0 border-t ${OVERLAY_BORDER} data-[state=closed]:slide-out-to-bottom data-[state=open]:slide-in-from-bottom`,
        left: `inset-y-0 left-0 h-full max-w-full border-r ${OVERLAY_BORDER} data-[state=closed]:slide-out-to-left data-[state=open]:slide-in-from-left`,
        right: `inset-y-0 right-0 h-full max-w-full border-l ${OVERLAY_BORDER} data-[state=closed]:slide-out-to-right data-[state=open]:slide-in-from-right`,
      },
      size: {
        default: PANEL_WIDTH.default,
        wide: PANEL_WIDTH.wide,
      },
    },
    defaultVariants: {
      side: "right",
      size: "default",
    },
  },
)

interface SheetContentProps
  extends
    React.ComponentPropsWithoutRef<typeof SheetPrimitive.Content>,
    VariantProps<typeof sheetVariants> {}

const SheetContent = React.forwardRef<
  React.ElementRef<typeof SheetPrimitive.Content>,
  SheetContentProps
>(({ side = "right", size, className, children, ...props }, ref) => (
  <SheetPortal>
    <SheetOverlay />
    <SheetPrimitive.Content
      ref={ref}
      className={cn(sheetVariants({ side, size }), className)}
      {...props}
    >
      {children}
      <SheetPrimitive.Close className="right-5 absolute top-4 flex h-7 w-7 items-center justify-center rounded-full text-palette-jungleGreen500 opacity-70 ring-offset-background transition-opacity hover:bg-accent hover:text-palette-jungleGreen900 hover:opacity-100 focus:outline-none focus:ring-2 focus:ring-ring focus:ring-offset-2 disabled:pointer-events-none">
        <X className="h-4 w-4" />
        <span className="sr-only">Close</span>
      </SheetPrimitive.Close>
    </SheetPrimitive.Content>
  </SheetPortal>
))
SheetContent.displayName = SheetPrimitive.Content.displayName

/** `-mx-5 px-5` so a `border-b` here reaches both edges of the rail. */
const SheetHeader = ({ className, ...props }: React.HTMLAttributes<HTMLDivElement>) => (
  <div
    className={cn(
      "-mx-5 -mt-5 mb-4 flex flex-col space-y-1 border-b px-5 pb-4 pt-5 text-left",
      OVERLAY_BORDER,
      className,
    )}
    {...props}
  />
)
SheetHeader.displayName = "SheetHeader"

const SheetFooter = ({ className, ...props }: React.HTMLAttributes<HTMLDivElement>) => (
  <div
    className={cn("flex flex-col-reverse sm:flex-row sm:justify-end sm:space-x-2", className)}
    {...props}
  />
)
SheetFooter.displayName = "SheetFooter"

const SheetTitle = React.forwardRef<
  React.ElementRef<typeof SheetPrimitive.Title>,
  React.ComponentPropsWithoutRef<typeof SheetPrimitive.Title>
>(({ className, ...props }, ref) => (
  <SheetPrimitive.Title
    ref={ref}
    className={cn("text-lg font-semibold text-foreground", className)}
    {...props}
  />
))
SheetTitle.displayName = SheetPrimitive.Title.displayName

const SheetDescription = React.forwardRef<
  React.ElementRef<typeof SheetPrimitive.Description>,
  React.ComponentPropsWithoutRef<typeof SheetPrimitive.Description>
>(({ className, ...props }, ref) => (
  <SheetPrimitive.Description
    ref={ref}
    className={cn("text-sm text-palette-jungleGreen500", className)}
    {...props}
  />
))
SheetDescription.displayName = SheetPrimitive.Description.displayName

export {
  Sheet,
  SheetClose,
  SheetContent,
  SheetDescription,
  SheetFooter,
  SheetHeader,
  SheetOverlay,
  SheetPortal,
  SheetTitle,
  SheetTrigger,
}
